<template>
	<pageWithNav>
		<slideOutModal @close="
			() => {
				showUpdateWorkflowModal = false;
			}
		" :visible="showUpdateWorkflowModal">
			<createUpdateWorkflowForm :existingWorkflow="workflowToEdit" @operationComplete="workflowUpdated" />
		</slideOutModal>

		<el-dialog title="Create New Version" :visible.sync="showCreateVersionModal" width="420px"
			:close-on-click-modal="false">
			<div class="dialog-field">
				<label class="dialog-label">Parent version</label>
				<el-input v-model="createVersionForm.parentVersion" disabled />
			</div>

			<div class="dialog-field">
				<label class="dialog-label">New version name</label>
				<el-input v-model="createVersionForm.newVersion" placeholder="e.g. 1.1.0" />
			</div>

			<span slot="footer">
				<el-button @click="showCreateVersionModal = false">
					Cancel
				</el-button>
				<el-button type="primary" @click="confirmCreateNewVersion" :loading="operationInProgress">
					Save
				</el-button>
			</span>
		</el-dialog>

		<el-drawer title="Version History" :visible.sync="showVersionSidebar" direction="rtl" size="360px">
			<el-empty :image="require('../assets/empty.svg')" v-if="!versions || !versions.length"
				description="No Versions"></el-empty>
			<div class="timeline" :class="{ 'single-version': versions && versions.length === 1 }">
				<div v-for="v in versions" :key="v.versionId" class="timeline-item"
					:class="{ active: v.versionId === form.version }" @click="selectVersion(v.versionId)">
					<span class="timeline-dot"></span>

					<div class="timeline-content">
						<div class="timeline-title">
							<span>
								{{ v.versionName && v.versionName !== v.version
									? `${v.versionName} (${v.version})`
									: v.version
								}}
							</span>

							<div class="timeline-actions">
								<span v-if="v.isActive" class="status-pill active">
									Active
								</span>

								<i v-if="!v.isActive" class="el-icon-delete delete-version-icon"
									@click.stop="confirmDeleteVersion(v)"></i>
							</div>
						</div>

						<div class="timeline-date">
							Created on:
							{{ new Date(v.createdAt).toLocaleString() }}
						</div>
					</div>
				</div>
			</div>
		</el-drawer>

		<innerPage pagePadding="0px">
			<div slot="header">
				<div class="headerContent">
					<div style="margin-left: 22px; display: inline-flex; align-items: center">
						<div id="wf-tour-workflow-active-toggle">
						<workflowActiveSwitchElement
							:disabled="$route.name !== 'workflow' || workflow.access === 'read'"
							@highlightNodeErrorId="highlightNodeErrorId" :key="workflow.id" :workflow="workflow"
							:refreshVersions="refreshVersions" customStyle="margin-right: 20px" />
						</div>
						<h3 style="display: inline-flex; align-items: center; gap: 8px;">
							{{ this.workflow.name || $route.params.name }}

							<span v-if="this.form.version" class="current-version-badge">
								{{ this.workflow.version }}
							</span>
						</h3>
						<transition name="el-fade-in-linear">
							<el-tag type="warning" style="margin-left: 20px" v-show="workflow.isActive">Workflow is
								active</el-tag>
						</transition>
					</div>
					<div style="margin-left: auto; display: flex; align-items: center; margin-right: 20px">
						<transition name="el-fade-in-linear">
							<el-tag type="primary" style="margin-right: 20px"
								v-show="workflow.access && workflow.access !== 'admin'">
								Workflow owned by {{ workflow.ownedBy }}
							</el-tag>
						</transition>
						<div style="display: inline-flex; margin-right: 20px;">
							<el-button id="wf-tour-save-btn" type="primary" :loading="operationInProgress"
								:disabled="workflow.isActive || $route.name !== 'workflow' || workflow.access === 'read'"
								@click="saveWorkflowChanges()"
								style="border-top-right-radius: 0; border-bottom-right-radius: 0;">
								<i style="margin-right: 10px" class="icon-workflow"></i>
								Save Workflow Changes
							</el-button>

							<el-dropdown trigger="click"
								:disabled="workflow.isActive || $route.name !== 'workflow' || workflow.access === 'read'">
								<el-button id="wf-tour-version-arrow-btn" type="primary" icon="el-icon-arrow-down"
									:disabled="workflow.isActive || $route.name !== 'workflow' || workflow.access === 'read'"
									style="
										border-top-left-radius: 0;
										border-bottom-left-radius: 0;
										margin-left: 1px;
										padding-left: 12px;
										padding-right: 12px;
										min-height: 42px;
									" />

								<el-dropdown-menu slot="dropdown">
									<el-dropdown-item @click.native="openCreateVersionModal">
										Save as new version
									</el-dropdown-item>
								</el-dropdown-menu>
							</el-dropdown>
						</div>
						<el-tooltip content="Workflow Settings" placement="top" effect="dark">
							<el-button id="wf-tour-settings-btn"
								:disabled="workflow.isActive || $route.name !== 'workflow' || workflow.access === 'read'"
								@click="launchEditModal(workflow)" plain type="primary" style="font-size: 20px"
								icon="el-icon-set-up" circle></el-button>
						</el-tooltip>
						<el-tooltip content="Workflow Version History" placement="top" effect="dark">
							<el-button id="wf-tour-versions-btn" :disabled="$route.name !== 'workflow' || workflow.access === 'read'"
								@click="showVersionSidebar = true" plain type="primary" circle class="history-button">
								<svg width="18" height="18" viewBox="0 0 18 18" fill="none" class="history-icon"
									xmlns="http://www.w3.org/2000/svg">
									<path
										d="M10 2.5C14.1423 2.5 17.5 5.85775 17.5 10C17.5 14.1423 14.1423 17.5 10 17.5C5.85775 17.5 2.5 14.1423 2.5 10H4C4 13.3135 6.6865 16 10 16C13.3135 16 16 13.3135 16 10C16 6.6865 13.3135 4 10 4C7.9375 4 6.118 5.04025 5.03875 6.625H7V8.125H2.5V3.625H4V5.5C5.368 3.6775 7.54675 2.5 10 2.5ZM10.75 6.25V9.68875L13.1823 12.121L12.121 13.1823L9.25 10.3098V6.25H10.75Z"
										fill="var(--icon-sub-600)" />
								</svg>
							</el-button>
						</el-tooltip>
					</div>
				</div>
			</div>
			<div style="height: 100%" slot="page">
				<el-container style="height: inherit">
					<el-aside style="height: inherit" width="80px">
						<innerNavSideBar />
					</el-aside>
					<el-main style="padding: 0px">
						<workflowCanvas :readOnlyMode="workflow.access === 'read' ? true : false"
							@saveWorkflowOperationCompleted="operationInProgress = false" ref="workflowCanvas"
							@new-version-created="onNewVersionCreated" :isNewVersion="isNewVersion"
							:workflowObject="workflow" v-if="$route.name === 'workflow'"
							@refresh-workflow="refreshWorkflowEditor" />
						<workflowExecutionsCanvas v-if="$route.name === 'executions'" />
					</el-main>
				</el-container>
			</div>
		</innerPage>
		<StickyNotes v-if="workflowCanvasRef" :workflowCanvas="workflowCanvasRef"
			@refresh-workflow="refreshWorkflowEditor" />
		<workflowTour
			:visible.sync="workflowTourVisible"
			:steps="workflowTourSteps"
			:initialStepIndex="workflowTourInitialIndex"
			@completed="onTourCompleted"
			@skipped="onTourSkipped"
			@ended="onTourSkipped"
			@step-change="handleTourStepChange"
		/>
	</pageWithNav>
</template>

<script>
import slideOutModal from "../components/slideOutModal.vue";
import createUpdateWorkflowForm from "../components/createUpdateWorkflowForm.vue";
import pageWithNav from "../components/pageWithNav.vue";
import innerPage from "../components/innerPage.vue";
import innerNavSideBar from "../components/innerNavSideBar.vue";
import workflowCanvas from "../components/workflowCanvas.vue";
import workflowExecutionsCanvas from "@/components/workflowExecutionsCanvas.vue";
import workflowActiveSwitchElement from "../components/workflowActiveSwitchElement.vue";
import StickyNotes from "../components/stickyNotes.vue";
import workflowTour from "../components/workflowTour.vue";

import { mapGetters, mapActions } from "vuex";
import _ from "lodash";
import { httpHelper } from "../utils/httpHelper.js";
export default {
	computed: {
		...mapGetters(["returnActiveWorkflow", "getSelectedWorkflowVersion", "isAdmin"]),
		workflowTourSteps() {
			return [
				{
					target: "#workflowCanvas",
					title: "Open integrations quickly",
					content: "Double-click anywhere on the canvas to open integrations.",
					placement: "top",
					cardPlacement: "top",
					advanceWhen: { type: "dblclick_target", selector: "#workflowCanvas" }
				},
				{
					target: "#wf-tour-node-tabs .el-tabs__item",
					targetTextContains: "Triggers",
					title: "Step 1: Go to Triggers tab",
					content: "Click the 'Triggers' tab to browse trigger integrations.",
					placement: "right",
					cardPlacement: "right",
					waitForTarget: true,
					advanceWhen: { type: "click_text_contains", selector: "#wf-tour-node-tabs .el-tabs__item", text: "Triggers" }
				},
				{
					target: "#wf-tour-node-search input",
					title: "Step 1: Search Webhook",
					content: "Type 'webhook' in the search box to find the Webhook Trigger integration, then click <b>Next</b>.",
					placement: "right",
					cardPlacement: "top",
					waitForTarget: true,
					manualAdvance: true,
					advanceWhen: { type: "input_contains", selector: "#wf-tour-node-search input", value: "webhook" }
				},
				{
					target: "#webhookTrigger",
					title: "Step 1: Add Webhook Trigger",
					content: "Click or drag this integration row onto the canvas to add the Webhook Trigger node.",
					placement: "right",
					cardPlacement: "top",
					waitForTarget: true,
					showDragHint: true,
					manualAdvance: true
				},
				{
					target: "#wf-tour-node-tabs .el-tabs__item",
					targetTextContains: "Actions",
					title: "Step 2: Go to Actions tab",
					content: "Click the 'Actions' tab to browse action integrations.",
					placement: "right",
					cardPlacement: "top",
					waitForTarget: true,
					advanceWhen: { type: "click_text_contains", selector: "#wf-tour-node-tabs .el-tabs__item", text: "Actions" }
				},
				{
					target: "#wf-tour-node-search input",
					title: "Step 2: Search Send Email",
					content: "Type 'send email' in the search box to find the Send Email integration, then click <b>Next</b>.",
					placement: "right",
					cardPlacement: "top",
					waitForTarget: true,
					manualAdvance: true,
					advanceWhen: { type: "input_contains", selector: "#wf-tour-node-search input", value: "send email" }
				},
				{
					target: "#emailSend",
					title: "Step 2: Add Send Email",
					content: "Click or drag this integration row onto the canvas to add the Send Email node.",
					placement: "right",
					cardPlacement: "top",
					waitForTarget: true,
					showDragHint: true,
					manualAdvance: true
				},
				{
					target: "#wf-tour-node-search input",
					title: "Step 3: Search Respond to Webhook",
					content: "Clear the search and type 'respond' to find the Respond to Webhook integration, then click <b>Next</b>.",
					placement: "right",
					cardPlacement: "top",
					waitForTarget: true,
					manualAdvance: true,
					advanceWhen: { type: "input_contains", selector: "#wf-tour-node-search input", value: "respond" }
				},
				{
					target: "#respondToWebhook",
					title: "Step 3: Add Respond to Webhook",
					content: "Click or drag this integration row onto the canvas to add the Respond to Webhook node.",
					placement: "right",
					cardPlacement: "top",
					waitForTarget: true,
					showDragHint: true,
					manualAdvance: true
				},
				{
					target: "#workflowCanvas",
					title: "Connect the nodes",
					content: "Now connect the 3 nodes in order.\n\nHover on a node to reveal the arrow icon on its right side, then click and drag from that arrow to the next node.\n\nConnect: Webhook Trigger → Send Email → Respond to Webhook.\n\nOnce all connections are made, click Next.",
					placement: "top",
					cardPlacement: "center-right",
					manualAdvance: true,
					showConnectAnimation: true
				},
				{
					target: "#wf-tour-save-btn",
					title: "Save Workflow Changes",
					content: "Great! Your nodes are connected. Now click 'Save Workflow Changes' to persist your progress.",
					placement: "bottom",
					cardPlacement: "bottom",
					lowProfileOverlay: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-save-btn" }
				},
				{
					target: "#stickyNotebtn",
					title: "Control Bar: Sticky Note",
					content: "This button adds a Sticky Note to your canvas. Use them to document logic, leave reminders, or annotate sections for your team. Click the button to try it out, then click Next.",
					placement: "top",
					cardPlacement: "top",
					manualAdvance: true,
					lowProfileOverlay: true,
					waitForTarget: true
				},
				{
					target: "#newNodeBtn",
					title: "Control Bar: Add Integration",
					content: "Use this button to open the integration selector. You can browse and add nodes here as an alternative to double-clicking the canvas. Click it to see, then click Next.",
					placement: "top",
					cardPlacement: "top",
					manualAdvance: true,
					lowProfileOverlay: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-grid-btn",
					title: "Control Bar: Toggle Grid",
					content: "Toggles a snap-to-grid overlay. When enabled, nodes automatically align to grid lines for a cleaner, more organized layout.",
					placement: "top",
					cardPlacement: "top",
					manualAdvance: true,
					lowProfileOverlay: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-connector-btn",
					title: "Control Bar: Connector Type",
					content: "Switches the connector style between smooth Bezier curves and right-angle Taxi lines. Pick whichever makes your workflow easier to read.",
					placement: "top",
					cardPlacement: "top",
					manualAdvance: true,
					lowProfileOverlay: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-fit-btn",
					title: "Control Bar: Fit Graph",
					content: "Automatically zoom and pan so that every node in your workflow fits neatly inside the visible canvas area.",
					placement: "top",
					cardPlacement: "top",
					manualAdvance: true,
					lowProfileOverlay: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-img-btn",
					title: "Control Bar: Save as Image",
					content: "Exports a snapshot of your workflow canvas as a PNG image — perfect for documentation or sharing.",
					placement: "top",
					cardPlacement: "top",
					manualAdvance: true,
					lowProfileOverlay: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-zoom-in-btn",
					title: "Control Bar: Zoom In",
					content: "Click the + button to zoom into your workflow and see details more clearly.",
					placement: "top",
					cardPlacement: "top",
					manualAdvance: true,
					lowProfileOverlay: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-zoom-out-btn",
					title: "Control Bar: Zoom Out",
					content: "Click the − button to zoom out and see more of your workflow overview.",
					placement: "top",
					cardPlacement: "top",
					manualAdvance: true,
					lowProfileOverlay: true,
					waitForTarget: true
				},
				{
					target: "#workflowCanvas",
					title: "Open Webhook node",
					content: "Now click on the Webhook Trigger node on the canvas to open its configuration panel on the right side.\n\nLook for the node labelled 'Webhook Trigger' and single-click it.",
					placement: "top",
					lowProfileOverlay: true,
					advanceWhen: { type: "click_target", selector: "#workflowCanvas" }
				},
				{
					target: "#wf-tour-output-panel-arrow",
					title: "Open test data panel",
					content: "Click this outgoing-data arrow to open the right panel where you can insert webhook test JSON.",
					waitForTarget: true,
					recoverToTitle: "Open Webhook node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-output-panel-arrow" }
				},
				{
					target: "#wf-tour-insert-test-data-btn",
					title: "Insert webhook test JSON",
					content: "Click 'Insert test json data' to open the JSON editor.",
					waitForTarget: true,
					recoverToTitle: "Open Webhook node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-insert-test-data-btn" }
				},
				{
					target: "#wf-tour-webhook-test-header",
					title: "Paste sample JSON",
					content: "Copy this sample JSON and paste it into the editor below. Once pasted, click <b>Next</b>.",
					waitForTarget: true,
					recoverToTitle: "Open Webhook node",
					cardPlacement: "bottom",
					manualAdvance: true,
					lowProfileOverlay: true,
					codeSnippet: "[\n  {\n    \"fromEmail\": \"hello@example.com\",\n    \"toEmail\": \"qa@example.com\",\n    \"subject\": \"Centiloquy onboarding run\",\n    \"text\": \"Hello from workflow tour\"\n  }\n]"
				},
				{
					target: "#wf-tour-insert-test-data-btn",
					title: "Save test JSON",
					content: "Click <b>Save</b> on test data to persist this sample payload.",
					waitForTarget: true,
					recoverToTitle: "Open Webhook node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-insert-test-data-btn" }
				},
				{
					target: "#wf-tour-node-close-btn",
					title: "Close Webhook panel",
					content: "Click the close arrow to collapse this node panel after saving test data.",
					waitForTarget: true,
					recoverToTitle: "Open Webhook node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-node-close-btn" }
				},
				{
					target: "#workflowCanvas",
					title: "Open Send Email node",
					content: "Click the Send Email node to configure mappings from webhook data.",
					placement: "top",
					lowProfileOverlay: true,
					advanceWhen: { type: "click_target", selector: "#workflowCanvas" }
				},
				{
					target: "#wf-tour-input-panel-arrow",
					title: "Open incoming data",
					content: "Click this left arrow to open incoming data from previous node.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-input-panel-arrow" }
				},
				{
					target: "#wf-tour-make-variable-btn-fromEmail",
					title: "Sender Email: make variable",
					content: "Click 'Make input variable' for Sender's Email.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-make-variable-btn-fromEmail" }
				},
				{
					target: "#wf-tour-param-fromEmail .editor__content",
					title: "Sender Email: focus input",
					content: "Click the Sender's Email variable input box once to focus it.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-param-fromEmail .editor__content" }
				},
				{
					target: "#wf-tour-incoming-json-data .vjs-key",
					targetTextContains: "fromEmail",
					title: "Sender Email: select key",
					content: "From incoming JSON, click the `fromEmail` key to insert its reference.",
					waitForTarget: true,
					recoverToTitle: "Open incoming data",
					advanceWhen: { type: "click_text_contains", selector: "#wf-tour-incoming-json-data .vjs-key", text: "fromEmail" }
				},
				{
					target: "#wf-tour-make-variable-btn-toEmail",
					title: "Scroll Down",
					content: "The To Email field is further down. Please scroll the panel down until you see the 'Email To' section.",
					showScrollArrow: "down",
					manualAdvance: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-make-variable-btn-toEmail",
					title: "Email To: make variable",
					content: "Click 'Make input variable' for Email to.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-make-variable-btn-toEmail" }
				},
				{
					target: "#wf-tour-param-toEmail .editor__content",
					title: "Email To: focus input",
					content: "Click the Email to variable input box once to focus it.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-param-toEmail .editor__content" }
				},
				{
					target: "#wf-tour-incoming-json-data .vjs-key",
					targetTextContains: "toEmail",
					title: "Email To: select key",
					content: "From incoming JSON, click the `toEmail` key to insert its reference.",
					waitForTarget: true,
					recoverToTitle: "Open incoming data",
					advanceWhen: { type: "click_text_contains", selector: "#wf-tour-incoming-json-data .vjs-key", text: "toEmail" }
				},
				{
					target: "#wf-tour-make-variable-btn-subject",
					title: "Scroll Down",
					content: "Scroll down a bit more to find the Email Subject field.",
					showScrollArrow: "down",
					manualAdvance: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-make-variable-btn-subject",
					title: "Subject: make variable",
					content: "Find the Email Subject field and click 'Make input variable'. The tour will automatically scroll it into view for you.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					autoScroll: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-make-variable-btn-subject" }
				},
				{
					target: "#wf-tour-param-subject .editor__content",
					title: "Subject: focus input",
					content: "Click the Subject variable input box once to focus it.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					autoScroll: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-param-subject .editor__content" }
				},
				{
					target: "#wf-tour-incoming-json-data .vjs-key",
					targetTextContains: "subject",
					title: "Subject: select key",
					content: "From incoming JSON, click the `subject` key to insert its reference.",
					waitForTarget: true,
					recoverToTitle: "Open incoming data",
					advanceWhen: { type: "click_text_contains", selector: "#wf-tour-incoming-json-data .vjs-key", text: "subject" }
				},
				{
					target: "#wf-tour-make-variable-btn-text",
					title: "Scroll Down",
					content: "The Plain Text (body) field is at the bottom. Scroll down to reach it.",
					showScrollArrow: "down",
					manualAdvance: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-make-variable-btn-text",
					title: "Body: make variable",
					content: "Find the Plain Text (email body) field and click 'Make input variable'.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					autoScroll: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-make-variable-btn-text" }
				},
				{
					target: "#wf-tour-param-text .editor__content",
					title: "Body: focus input",
					content: "Click the body variable input box once to focus it.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					autoScroll: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-param-text .editor__content" }
				},
				{
					target: "#wf-tour-incoming-json-data .vjs-key",
					targetTextContains: "text",
					title: "Body: select key",
					content: "From incoming JSON, click the `text` key to insert its reference.",
					waitForTarget: true,
					recoverToTitle: "Open incoming data",
					advanceWhen: { type: "click_text_contains", selector: "#wf-tour-incoming-json-data .vjs-key", text: "text" }
				},
				{
					target: "#wf-tour-node-save-btn",
					title: "Save Send Email node",
					content: "Click Save Changes on Send Email node.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-node-save-btn" }
				},
				{
					target: "#wf-tour-configure-credential-btn",
					title: "Scroll Up",
					content: "Finally, scroll back to the top of the panel to see the Credential configuration.",
					showScrollArrow: "up",
					manualAdvance: true,
					waitForTarget: true
				},
				{
					target: "#wf-tour-configure-credential-btn",
					title: "Configure credential",
					content: "The tour has scrolled back up to find the 'Configure New Credential' button. Click it to open the credential configuration.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					autoScroll: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-configure-credential-btn" }
				},
				{
					target: "#wf-tour-credential-modal",
					title: "Enter Credentials",
					content: "Please fill in your credential values here. If you don't have them right now, this is an optional step—simply click 'Next' to move on.",
					waitForTarget: true,
					manualAdvance: true
				},
				{
					target: "#wf-tour-node-close-btn",
					title: "Close Send Email panel",
					content: "Great job! Now click this back arrow to close the configuration panel and return to your workflow canvas.",
					waitForTarget: true,
					recoverToTitle: "Open Send Email node",
					advanceWhen: { type: "click_target", selector: "#wf-tour-node-close-btn" }
				},
				{
					target: "#wf-tour-workflow-active-toggle",
					title: "Turn workflow on",
					content: "Turn on the workflow to allow live/test runs. Wait for the confirmation popup and click 'Start Workflow' or 'OK'.",
					lowProfileOverlay: true,
					advanceWhen: { 
						type: "click_target", 
						selector: ".el-message-box__btns .el-button--primary, .el-dialog__footer .el-button--primary" 
					}
				},
				{
					target: "#workflowCanvas",
					title: "Hover & Run Webhook",
					content: "Hover your mouse over the <strong>Webhook Trigger</strong> node on the canvas, then click the **Play** icon to run the workflow from this node.",
					cardPlacement: "center-right",
					lowProfileOverlay: true,
					advanceWhen: { type: "click_target", selector: ".runWorkflowBtn" }
				},
				{
					target: "#workflowCanvas",
					title: "Executing...",
					content: "The workflow is executing. Once the progress bar disappears, please click <strong>Next</strong> to proceed.",
					waitForTarget: true,
					manualAdvance: true,
					cardPlacement: "center-right"
				},
				{
					target: "#wf-tour-executions-tab",
					title: "Open executions",
					content: "Great! Now switch to the <strong>Executions</strong> tab to see your results.",
					waitForTarget: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-executions-tab" }
				},
				{
					target: ".nodeExecutionsList .item",
					title: "Select Execution",
					content: "Click on the first execution in the list to load its specific data onto the canvas.",
					waitForTarget: true,
					advanceWhen: { type: "click_target", selector: ".nodeExecutionsList .item" }
				},
				{
					target: "#wf-tour-nav-integrations",
					title: "Main Menu: Integrations",
					content: "In the main sidebar, you can find the <strong>Integrations</strong> menu. <br><br>Click the icon or <strong>Next</strong> to continue the tour overview.",
					waitForTarget: true,
					manualAdvance: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-nav-integrations" },
					preventTargetDefault: true
				},
				{
					target: "#wf-tour-nav-templates",
					title: "Main Menu: Templates",
					content: "Check out the <strong>Templates</strong> section for pre-built automation workflows. <br><br>Click the icon or <strong>Next</strong> to continue.",
					waitForTarget: true,
					manualAdvance: true,
					advanceWhen: { type: "click_target", selector: "#wf-tour-nav-templates" },
					preventTargetDefault: true
				},
				...(this.isAdmin ? [
					{
						target: "#wf-tour-nav-admin",
						title: "Main Menu: Admin",
						content: "The <strong>Admin</strong> section allows you to manage organization settings and user roles. <br><br>Click <strong>Next</strong> to finish your tour!",
						waitForTarget: true,
						manualAdvance: true,
						advanceWhen: { type: "click_target", selector: "#wf-tour-nav-admin" },
						preventTargetDefault: true
					}
				] : [])
			]
		},
	},
	components: {
		slideOutModal,
		createUpdateWorkflowForm,
		pageWithNav,
		innerPage,
		innerNavSideBar,
		workflowCanvas,
		workflowActiveSwitchElement,
		workflowExecutionsCanvas,
		StickyNotes,
		workflowTour
	},
	data() {
		return {
			workflowToEdit: null,
			showUpdateWorkflowModal: false,
			operationInProgress: false,
			showVersionSidebar: false,
			workflow: {},
			versions: [],
			isNewVersion: false,
			form: {
				version: null,
			},
			showCreateVersionModal: false,
			createVersionForm: {
				parentVersion: "1.0.0",
				newVersion: ""
			},
			workflowCanvasRef: null,
			workflowTourVisible: false,
			workflowTourInitialIndex: 0,
		};
	},
	watch: {
		"$route.name": async function () {
			if (this.$route.name === "workflow") {
				await this.load();
			}
		},
	},
	async mounted() {
		this.workflowCanvasRef = this.$refs.workflowCanvas;
		if (this.$route.name === "workflow") {
			const workflowId = this.$route.params.workflowId;

			const storedVersion = this.getSelectedWorkflowVersion(workflowId);

			if (!storedVersion) {
				const versions = await this.fetchWorkflowVersionsMETHOD(workflowId);

				if (versions && versions.length) {
					this.$store.commit("SET_WORKFLOW_VERSION", {
						workflowId,
						versionId: versions[0].versionId
					});
				}
			}

			await this.load();
		}

		this.maybeAutoStartTour();
	},
	async updated() {
		this.workflowCanvasRef = this.$refs.workflowCanvas;
	},
	methods: {
		...mapActions(["returnSpecificWorkflowMETHOD", "fetchWorkflowVersionsMETHOD", "deleteVersion"]),
		maybeAutoStartTour() {
			try {
				const inProgress = localStorage.getItem("workflowTourInProgress") === "true";
				const phase = localStorage.getItem("workflowTourPhase");
				if (!inProgress || phase !== "editor") return;
				setTimeout(() => {
					this.workflowTourVisible = true;
				}, 700);
			} catch (e) { console.log(e); }
		},
		async persistTourStatus(status) {
			try {
				await httpHelper("RUNTIME", "POST", "security/workflowTour", { status });
				this.$cookies.set("workflowTourStatus", status, "60d");
			} catch (e) { console.log(e); }
		},
		async onTourCompleted() {
			localStorage.removeItem("workflowTourInProgress");
			localStorage.removeItem("workflowTourPhase");
			localStorage.removeItem("workflowTourStartRequested");
			this.persistTourStatus("completed");

			const workflowId = this.$route.params.workflowId;
			const versionId = this.form.version;

			this.$store.commit("SET_WORKFLOW_VERSION", {
				workflowId,
				versionId
			})

			await this.returnSpecificWorkflowMETHOD({
				workflowId,
				versionId
			})

			this.workflow = _.cloneDeep(this.returnActiveWorkflow)
			this.workflow.versionId = versionId

			this.showVersionSidebar = false

			this.$nextTick(() => {
				this.$refs.workflowCanvas?.loadWorkflow()
			})
		},
		onTourSkipped() {
			localStorage.removeItem("workflowTourInProgress");
			localStorage.removeItem("workflowTourPhase");
			localStorage.removeItem("workflowTourStartRequested");
			this.persistTourStatus("skipped");
		},
		handleTourStepChange(event) {
			if (!event) return;
			const { direction, step, index } = event;

			if (direction === "backward") {
				// Close any UI elements that might have been opened by forward steps
				this.showVersionSidebar = false;
				this.showUpdateWorkflowModal = false;
				this.showCreateVersionModal = false;
			}

			// 1. Keep the bottom control-bar walkthrough stable:
			// after "Add Integration", close the selector so next buttons are focusable/highlightable.
			const stepTitle = String(step?.title || "");
			const isControlBarStep = stepTitle.startsWith("Control Bar:");
			const isAddIntegrationStep = stepTitle === "Control Bar: Add Integration";
			if (isControlBarStep && !isAddIntegrationStep) {
				const canvas = this.$refs.workflowCanvas;
				if (canvas && canvas.nodeSelectionMenuVisible) {
					canvas.nodeSelectionMenuVisible = false;
					canvas.nodeSelectionFilter = null;
				}
			}

			// 2. Close credential modal when moving away from the "Enter Credentials" step
			const prevIndex = index - (direction === "forward" ? 1 : -1);
			if (prevIndex >= 0 && prevIndex < this.workflowTourSteps.length) {
				const prevStep = this.workflowTourSteps[prevIndex];
				if (prevStep.title === "Enter Credentials") {
					try {
						const form = this.$refs.workflowCanvas?.$refs?.nodeContentsModal?.$refs?.integrationParamsConfigurationForm;
						if (form) form.showConfigureCredentialsModal = false;
					} catch (e) {
						console.error("Tour: failed to auto-close credential modal", e);
					}
				}
			}
		},
		async refreshWorkflowEditor() {
			const workflowId = this.$route.params.workflowId;
			const versionId = this.form.version;

			await this.returnSpecificWorkflowMETHOD({
				workflowId,
				versionId
			});

			this.workflow = _.cloneDeep(this.returnActiveWorkflow);

			this.$nextTick(() => {
				this.$refs.workflowCanvas?.loadWorkflow();
			});
		},
		confirmDeleteVersion(version) {
			this.$confirm(
				"Do you wish to delete this version. Continue?",
				"Warning",
				{
					confirmButtonText: "Delete",
					cancelButtonText: "Cancel",
					type: "warning",
				}
			)
				.then(async () => {
					const result = await this.deleteVersion({ versionId: version.versionId, workflowId: this.$route.params.workflowId });

					if (result?.success) {
						this.$notify({
							title: "Success",
							message: "Workflow version deleted",
							type: "success",
							position: "bottom-left",
						});
						await this.refreshVersions();

						if (!this.versions.length) {
							await this.returnSpecificWorkflowMETHOD({
								workflowId: this.$route.params.workflowId
							});

							this.form.version = null;
							this.workflow = _.cloneDeep(this.returnActiveWorkflow);
						} else {
							const nextVersion =
								this.versions.find(v => v.isActive) || this.versions[0];

							this.form.version = nextVersion.versionId;

							this.$store.commit("SET_WORKFLOW_VERSION", {
								workflowId: this.$route.params.workflowId,
								versionId: nextVersion.versionId,
							});

							await this.returnSpecificWorkflowMETHOD({
								workflowId: this.$route.params.workflowId,
								versionId: nextVersion.versionId,
							});

							this.workflow = _.cloneDeep(this.returnActiveWorkflow);
							this.workflow.versionId = nextVersion.versionId;
						}

						this.$nextTick(() => {
							this.$refs.workflowCanvas?.loadWorkflow();
						});
					}
				})
				.catch(() => { });
		},
		async onNewVersionCreated(newVersionId) {
			const workflowId = this.$route.params.workflowId;

			this.$store.commit("SET_WORKFLOW_VERSION", {
				workflowId,
				versionId: newVersionId
			});

			this.isNewVersion = false;

			await this.returnSpecificWorkflowMETHOD({
				workflowId,
				versionId: newVersionId
			});

			this.workflow = _.cloneDeep(this.returnActiveWorkflow);

			this.workflow.versionId = newVersionId;

			this.$nextTick(() => {
				this.$refs.workflowCanvas?.loadWorkflow();
			});
		},
		async selectVersion(versionId) {
			const workflowId = this.$route.params.workflowId;

			this.$store.commit("SET_WORKFLOW_VERSION", {
				workflowId,
				versionId,
			});

			await this.returnSpecificWorkflowMETHOD({ workflowId, versionId });

			this.workflow = _.cloneDeep(this.returnActiveWorkflow);

			this.$nextTick(() => {
				this.$refs.workflowCanvas?.loadWorkflow();
			});
		},

		async load() {
			this.isNewVersion = false;
			const workflowId = this.$route.params.workflowId;
			this.versions = await this.fetchWorkflowVersionsMETHOD(workflowId);

			if (!this.versions.length) {
				await this.returnSpecificWorkflowMETHOD({
					workflowId
				});

				this.workflow = _.cloneDeep(this.returnActiveWorkflow);

				this.$nextTick(() => {
					this.$refs.workflowCanvas?.loadWorkflow();
				});

				return;
			}

			const active = this.versions.find(v => v.isActive === true) || this.versions[0];
			this.form.version = active.versionId;

			await this.returnSpecificWorkflowMETHOD({
				workflowId,
				versionId: this.form.version
			});

			this.workflow = _.cloneDeep(this.returnActiveWorkflow);

			this.$nextTick(() => {
				this.$refs.workflowCanvas?.loadWorkflow();
			});
		},
		async refreshVersions(selectActive = true) {
			this.isNewVersion = false;
			const workflowId = this.$route.params.workflowId;
			this.versions = await this.fetchWorkflowVersionsMETHOD(workflowId);

			if (!selectActive || !this.versions.length) return;

			const storedVersion = this.getSelectedWorkflowVersion(workflowId);

			const exists = storedVersion
				&& this.versions.find(v => v.versionId === storedVersion);

			this.form.version = exists
				? storedVersion
				: (this.versions.find(v => v.isActive)?.versionId || this.versions[0]).versionId;
		},
		async onWorkflowActivationChanged() {
			await this.refreshVersions();

			this.$nextTick(() => {
				this.$refs.workflowCanvas?.loadWorkflow(true);
			});
		},
		highlightNodeErrorId(e) {
			if (e.nodeId) {
				this.$refs.workflowCanvas.highlightNodeError(e);
			}
		},
		launchEditModal(workflow) {
			this.workflowToEdit = {
				workflowId: workflow.id,
				workflowLastUpdated: workflow.updatedAt,
				name: workflow.name,
				description: workflow.description,
				timezone: workflow.settings.timezone,
				workflowMode: workflow.settings.workflowMode,
				deactivateOnError: workflow.settings.deactivateOnError,
				disableExecutionLogs: workflow.settings.disableExecutionLogs,
				settings: workflow.settings,
			};
			this.showUpdateWorkflowModal = true;
		},
		workflowUpdated(workflow) {
			this.showUpdateWorkflowModal = false;
			this.workflow = workflow;
			this.$router.push(`/workflows/${workflow.id}/${workflow.name}`).catch(() => { });
			this.$nextTick(() => {
				this.$refs.workflowCanvas.loadWorkflow(true);
			});
		},
		async saveWorkflowChanges() {
			this.operationInProgress = true;
			await this.$refs.workflowCanvas.saveWorflowSurfaceChanges(this.isNewVersion, this.onWorkflowActivationChanged, this.createVersionForm.newVersion);
		},
		async saveAsNewVersion() {
			this.isNewVersion = true;
			await this.saveWorkflowChanges();
			this.isNewVersion = false;
		},
		openCreateVersionModal() {
			const currentVersion =
				this.versions.find(v => v.versionId === this.form.version)?.version || "1.0.0";

			this.createVersionForm.parentVersion = currentVersion;
			this.createVersionForm.newVersion = "";
			this.showCreateVersionModal = true;
		},

		async confirmCreateNewVersion() {
			this.showCreateVersionModal = false;

			this.isNewVersion = true;

			await this.saveWorkflowChanges();

			this.isNewVersion = false;
		}
	},
};
</script>

<style scoped>
.headerContent {
	display: flex;
	align-items: center;
	padding-top: 11px;
	padding-bottom: 10px;
}

.headerContent * {
	margin: 0px;
}

.viewSwitch {
	position: absolute;
	margin: auto;
	left: 0;
	right: 0;
	width: 209.77px;
	top: 0px;
}

.version-list {
	display: flex;
	flex-direction: column;
}

.version-item {
	padding: 12px 14px;
	cursor: pointer;
	border-bottom: 1px solid #e6e6e6;
}

.version-item:hover {
	background: #f5f7fa;
}

.version-item.active {
	background: #ecf5ff;
}

.version-title {
	font-weight: 600;
}

.version-date {
	font-size: 12px;
	opacity: 0.7;
	margin-top: 4px;
}

.timeline {
	position: relative;
	padding-left: 18px;
}

.timeline::before {
	content: "";
	position: absolute;
	left: 22px;
	top: 0;
	bottom: 0;
	width: 2px;
	background: #e5e7eb;
}

.timeline-item {
	position: relative;
	display: flex;
	align-items: flex-start;
	padding: 0px 0 14px 12px;
	cursor: pointer;
}

.timeline-item:hover .timeline-content {
	background: #f9fafb;
}

.timeline-dot {
	position: absolute;
	left: 0;
	top: 22px;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	background: #9ca3af;
}

.timeline-item.active .timeline-dot {
	background: #000000;
}

.timeline-content {
	padding: 8px 12px;
	border-radius: 6px;
	width: 100%;
	transition: background 0.2s;
}

.timeline-item.active .timeline-content {
	background: #f3f4f6;
}

.timeline-title {
	font-weight: 600;
	display: flex;
	align-items: center;
	gap: 6px;
	color: #111827;
}

.timeline-date {
	font-size: 12px;
	opacity: 0.7;
	margin-top: 4px;
	text-align: left;
	color: #6b7280;
}

.draft-badge {
	font-size: 11px;
	padding: 2px 6px;
	border-radius: 4px;
	background: #fef3c7;
	color: #92400e;
}

.history-button {
	padding-right: 14px !important;
}

.history-button .history-icon {
	fill: #000000;
}

.history-button:hover .history-icon {
	fill: #ffffff;
}

.history-button.focusing .history-icon {
	fill: #ffffff;
}

.dialog-field {
	margin-bottom: 16px;
}

.dialog-label {
	display: block;
	width: 100%;
	margin-bottom: 6px;
	font-size: 13px;
	font-weight: 500;
	color: #111827;
	text-align: left;
}

.status-pill {
	font-size: 11px;
	padding: 2px 8px;
	border-radius: 999px;
	font-weight: 500;
	line-height: 1.4;
}

.status-pill.active {
	background: #111827;
	color: #ffffff;
}

.current-version-badge {
	font-size: 12px;
	padding: 2px 8px;
	border-radius: 999px;
	background: #f3f4f6;
	color: #111827;
	font-weight: 500;
}

.history-empty {
	padding: 48px 24px;
	text-align: center;
}

.history-empty-title {
	font-size: 14px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 6px;
}

.history-empty-text {
	font-size: 13px;
	color: #6b7280;
	line-height: 1.5;
}

.timeline-actions {
	margin-left: auto;
	display: inline-flex;
	align-items: center;
	gap: 8px;
}

.delete-version-icon {
	font-size: 14px;
	color: #000000;
	cursor: pointer;
	opacity: 0;
	transition: opacity 0.15s ease;
}

.timeline-item:hover .delete-version-icon {
	opacity: 1;
}

.delete-version-icon:hover {
	color: #111827;
}

.timeline.single-version::before {
	display: none;
}

.timeline.single-version .timeline-dot {
	display: none;
}

.timeline.single-version .timeline-item {
	padding-left: 0;
	padding-right: 17px;
}
</style>
